import { Control, ObjectController, TextInput } from '@tempots/beatui'
import { Fragment } from '@tempots/dom'
import z from 'zod'
import { PhoneNumberInput } from './phone-number-input'
import { LooseEmailInput } from './loose-email-input'
import { LinkedInInput } from './linkedin-input'

// Wide enough for "Your Phone Number" — beatui's default fixed label column
// (7.5rem) truncates it. Matches the trial page's label column.
const LABEL_WIDTH = '11.5rem'

// The contact fields are NullableTextInputs whose cleared state is null —
// the schema must accept null or clearing a field makes the whole form
// silently unsubmittable (validation fails with no visible error).
export const agencyMemberSchema = z.object({
  recruiterName: z.string().min(1).max(255),
  recruiterEmail: z.string().nullish(),
  recruiterPhoneNumber: z.string().min(1).max(255).nullish(),
  recruiterLinkedIn: z.string().nullish(),
})

export type AgencyMemberFormValues = z.infer<typeof agencyMemberSchema>

export function AgencyMemberForm({
  controller,
  onFieldChange,
}: {
  controller: ObjectController<AgencyMemberFormValues>
  onFieldChange?: (
    fieldName: string,
    previousValue: string,
    newValue: string
  ) => void
}) {
  return Fragment(
    Control(TextInput, {
      layout: 'horizontal-fixed',
      labelWidth: LABEL_WIDTH,
      controller: controller.field('recruiterName'),
      label: 'Your Name',
      placeholder: 'J Doe',
      required: true,
      onChange: v => {
        if (onFieldChange) {
          onFieldChange(
            'recruiterName',
            controller.field('recruiterName').signal.value,
            v
          )
        }
      },
    }),
    Control(PhoneNumberInput, {
      layout: 'horizontal-fixed',
      labelWidth: LABEL_WIDTH,
      controller: controller.field('recruiterPhoneNumber'),
      label: 'Your Phone Number',
      onChange: v => {
        if (onFieldChange) {
          onFieldChange(
            'recruiterPhoneNumber',
            controller.field('recruiterPhoneNumber').signal.value ?? '',
            v ?? ''
          )
        }
      },
    }),
    Control(LooseEmailInput, {
      layout: 'horizontal-fixed',
      labelWidth: LABEL_WIDTH,
      controller: controller.field('recruiterEmail'),
      label: 'Your Email',
      onChange: v => {
        if (onFieldChange) {
          onFieldChange(
            'recruiterEmail',
            controller.field('recruiterEmail').signal.value ?? '',
            v ?? ''
          )
        }
      },
    }),
    Control(LinkedInInput, {
      layout: 'horizontal-fixed',
      labelWidth: LABEL_WIDTH,
      controller: controller.field('recruiterLinkedIn'),
      label: 'Your LinkedIn',
      onChange: v => {
        if (onFieldChange) {
          onFieldChange(
            'recruiterLinkedIn',
            controller.field('recruiterLinkedIn').signal.value ?? '',
            v ?? ''
          )
        }
      },
    })
  )
}
